import {
  fetchAttestation,
  waitForAttestation,
  approveUSDC,
  burnUSDC,
  getMessageBytes,
  isTransferCompleted,
  mintUSDC,
} from "./evm";
import {
  ApproveUSDCOnSolanaOptions,
  ApproveUSDCOptions,
  BurnUSDCOnSolanaOptions,
  BurnUSDCOptions,
  CCTPSdkConfigs,
  CCTPSdkConfigsSet,
  FetchAttestationOptions,
  GetMessageBytesOptions,
  IsTransferCompletedOptions,
  MintUSDCOnSolanaOptions,
  MintUSDCOptions,
  TransferUSDCOptions,
  TransferUSDCResult,
} from "./interface";
import {
  approveUSDCOnSolana,
  burnUSDCOnSolana,
  isTransferCompletedOnSolana,
  mintUSDCOnSolana,
} from "./solana";
import { CCTPDomain, getNetworkConfigs, waitForTransaction } from "./utils";
import merge from "lodash/merge";
import { Wallet } from "@coral-xyz/anchor/dist/cjs/provider";

export const defaultConfigs: CCTPSdkConfigs = {
  testnet: {
    irisApiHost: "https://iris-api-sandbox.circle.com",
    networks: [
      {
        domain: CCTPDomain.Ethereum,
        usdcContractAddress: "0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238",
        cctpMessageTransmitterContractAddress:
          "0x7865fAfC2db2093669d92c0F33AeEF291086BEFD",
        cctpMessengerContractAddress:
          "0x9f3B8679c73C2Fef8b59B4f3444d4e156fb70AA5",
        rpc: "https://rpc.ankr.com/eth_sepolia",
      },
      {
        domain: CCTPDomain.Avalanche,
        usdcContractAddress: "0x5425890298aed601595a70ab815c96711a31bc65",
        cctpMessageTransmitterContractAddress:
          "0xa9fb1b3009dcb79e2fe346c16a604b8fa8ae0a79",
        cctpMessengerContractAddress:
          "0xeb08f243e5d3fcff26a9e38ae5520a669f4019d0",
        rpc: "https://rpc.ankr.com/avalanche_fuji",
      },
      {
        domain: CCTPDomain.Polygon,
        usdcContractAddress: "0x41e94eb019c0762f9bfcf9fb1e58725bfb0e7582",
        cctpMessageTransmitterContractAddress:
          "0xe09A679F56207EF33F5b9d8fb4499Ec00792eA73",
        cctpMessengerContractAddress:
          "0x9f3B8679c73C2Fef8b59B4f3444d4e156fb70AA5",
        rpc: "https://rpc-amoy.polygon.technology",
      },
      {
        domain: CCTPDomain.Optimism,
        usdcContractAddress: "0x5fd84259d66Cd46123540766Be93DFE6D43130D7",
        cctpMessageTransmitterContractAddress:
          "0x7865fAfC2db2093669d92c0F33AeEF291086BEFD",
        cctpMessengerContractAddress:
          "	0x9f3B8679c73C2Fef8b59B4f3444d4e156fb70AA5",
        rpc: "https://rpc.ankr.com/optimism_sepolia",
      },
      {
        domain: CCTPDomain.Arbitrum,
        usdcContractAddress: "0x75faf114eafb1BDbe2F0316DF893fd58CE46AA4d",
        cctpMessageTransmitterContractAddress:
          "0xaCF1ceeF35caAc005e15888dDb8A3515C41B4872",
        cctpMessengerContractAddress:
          "	0x9f3B8679c73C2Fef8b59B4f3444d4e156fb70AA5",
        rpc: "https://rpc.ankr.com/arbitrum_sepolia",
      },
      {
        domain: CCTPDomain.Base,
        usdcContractAddress: "0x036CbD53842c5426634e7929541eC2318f3dCF7e",
        cctpMessageTransmitterContractAddress:
          "0x7865fAfC2db2093669d92c0F33AeEF291086BEFD",
        cctpMessengerContractAddress:
          "0x9f3B8679c73C2Fef8b59B4f3444d4e156fb70AA5",
        rpc: "https://rpc.ankr.com/base_sepolia",
      },
      {
        domain: CCTPDomain.Solana,
        usdcContractAddress: "4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU",
        cctpMessageTransmitterContractAddress:
          "CCTPmbSD7gX1bxKPAmg77w8oFzNFpaQiQUWD43TKaecd",
        cctpMessengerContractAddress:
          "CCTPiPYPc6AsJuwueEnWgSgucamXDZwBd53dQ11YiKX3",
        rpc: "https://api.devnet.solana.com",
      },
    ],
  },
  mainnet: {
    irisApiHost: "https://iris-api.circle.com",
    networks: [
      {
        domain: CCTPDomain.Ethereum,
        usdcContractAddress: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
        cctpMessageTransmitterContractAddress:
          "0x0a992d191deec32afe36203ad87d7d289a738f81",
        cctpMessengerContractAddress:
          "0xbd3fa81b58ba92a82136038b25adec7066af3155",
        rpc: "https://rpc.ankr.com/eth",
      },
      {
        domain: CCTPDomain.Avalanche,
        usdcContractAddress: "0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E",
        cctpMessageTransmitterContractAddress:
          "0x8186359af5f57fbb40c6b14a588d2a59c0c29880",
        cctpMessengerContractAddress:
          "0x6b25532e1060ce10cc3b0a99e5683b91bfde6982",
        rpc: "https://rpc.ankr.com/avalanche",
      },
      {
        domain: CCTPDomain.Optimism,
        usdcContractAddress: "0x0b2c639c533813f4aa9d7837caf62653d097ff85",
        cctpMessageTransmitterContractAddress:
          "0x4d41f22c5a0e5c74090899e5a8fb597a8842b3e8",
        cctpMessengerContractAddress:
          "0x2B4069517957735bE00ceE0fadAE88a26365528f",
        rpc: "https://rpc.ankr.com/optimism",
      },
      {
        domain: CCTPDomain.Arbitrum,
        usdcContractAddress: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
        cctpMessageTransmitterContractAddress:
          "0xC30362313FBBA5cf9163F0bb16a0e01f01A896ca",
        cctpMessengerContractAddress:
          "0x19330d10D9Cc8751218eaf51E8885D058642E08A",
        rpc: "https://rpc.ankr.com/arbitrum",
      },
      {
        domain: CCTPDomain.Base,
        usdcContractAddress: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
        cctpMessageTransmitterContractAddress:
          "0xAD09780d193884d503182aD4588450C416D6F9D4",
        cctpMessengerContractAddress:
          "0x1682Ae6375C4E4A97e4B583BC394c861A46D8962",
        rpc: "https://mainnet.base.org",
      },
      {
        domain: CCTPDomain.Polygon,
        usdcContractAddress: "0x3c499c542cef5e3811e1192ce70d8cc03d5c3359",
        cctpMessageTransmitterContractAddress:
          "0xF3be9355363857F3e001be68856A2f96b4C39Ba9",
        cctpMessengerContractAddress:
          "0x9daF8c91AEFAE50b9c0E69629D3F6Ca40cA3B3FE",
        rpc: "https://rpc.ankr.com/polygon",
      },
      {
        domain: CCTPDomain.Solana,
        usdcContractAddress: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
        cctpMessageTransmitterContractAddress:
          "CCTPmbSD7gX1bxKPAmg77w8oFzNFpaQiQUWD43TKaecd",
        cctpMessengerContractAddress:
          "CCTPiPYPc6AsJuwueEnWgSgucamXDZwBd53dQ11YiKX3",
        rpc: "https://api.mainnet-beta.solana.com",
      },
    ],
  },
};

export function CCTPSdk(configs?: CCTPSdkConfigs) {
  const mergedConfigs = merge({}, defaultConfigs, configs);

  return {
    testnet: () => {
      const testnetConfigs = mergedConfigs.testnet;

      if (!testnetConfigs) {
        throw new Error(`can't find testnet configs.`);
      }

      return generateActions(testnetConfigs);
    },
    mainnet: () => {
      const mainnetConfigs = mergedConfigs.mainnet;

      if (!mainnetConfigs) {
        throw new Error(`can't find mainnet configs.`);
      }

      return generateActions(mainnetConfigs);
    },
  };
}

async function transferUSDC(
  options: TransferUSDCOptions
): Promise<TransferUSDCResult> {
  const {
    signer,
    sourceDomain,
    destinationDomain,
    destinationAddress,
    amount,
    configsSet,
    destinationSigner,
    onApprove,
    onBurnUSDC,
    onGetMessageBytes,
    onFetchAttestation,
    onMintUSDC,
    beforeMintUSDC,
  } = options;

  const sourceNetworkConfigs = getNetworkConfigs(configsSet, sourceDomain);
  const destinationNetworkConfigs = getNetworkConfigs(
    configsSet,
    destinationDomain
  );

  if (onApprove) {
    onApprove();
  }

  if (sourceDomain === CCTPDomain.Solana) {
    if (!("publicKey" in signer)) {
      throw new Error("Signer is not a solana wallet");
    }
  }

  if (destinationDomain === CCTPDomain.Solana) {
    if (!("publicKey" in destinationSigner)) {
      throw new Error("Signer is not a solana wallet");
    }
  }

  const approveTx = await (sourceDomain === CCTPDomain.Solana
    ? approveUSDCOnSolana({
        wallet: signer as Wallet,
        amount,
        configsSet,
      })
    : approveUSDC({
        signer: signer as Exclude<typeof signer, Wallet>,
        amount,
        sourceDomain,
        configsSet,
      }));

  if (approveTx) {
    await waitForTransaction({
      rpc: sourceNetworkConfigs.rpc,
      domain: sourceDomain,
      txHash: approveTx.hash,
    });
  }

  if (onBurnUSDC) {
    onBurnUSDC();
  }

  const burnTx = await (sourceDomain === CCTPDomain.Solana
    ? burnUSDCOnSolana({
        wallet: signer as Wallet,
        destinationDomain,
        destinationAddress,
        amount,
        configsSet,
      })
    : burnUSDC({
        signer: signer as Exclude<typeof signer, Wallet>,
        sourceDomain,
        destinationDomain,
        destinationAddress,
        amount,
        configsSet,
      }));

  await waitForTransaction({
    rpc: sourceNetworkConfigs.rpc,
    domain: sourceDomain,
    txHash: burnTx.hash,
  });

  if (onGetMessageBytes) {
    onGetMessageBytes();
  }

  const { messageBytes, messageHash } = await getMessageBytes({
    sourceDomain,
    burnTxHash: burnTx.hash,
    configsSet,
  });

  if (onFetchAttestation) {
    onFetchAttestation();
  }

  const attestationSignature = await waitForAttestation({
    configsSet,
    messageHash,
  });

  if (beforeMintUSDC != null) {
    await beforeMintUSDC();
  }

  if (onMintUSDC) {
    onMintUSDC();
  }

  const mintTx = await (destinationDomain === CCTPDomain.Solana
    ? mintUSDCOnSolana({
        wallet: destinationSigner as Wallet,
        messageBytes,
        attestationSignature,
        configsSet,
      })
    : mintUSDC({
        signer: destinationSigner as Exclude<typeof destinationSigner, Wallet>,
        messageBytes,
        attestationSignature,
        destinationDomain,
        configsSet,
      }));

  await waitForTransaction({
    rpc: destinationNetworkConfigs.rpc,
    domain: destinationDomain,
    txHash: mintTx.hash,
  });

  return {
    approveTxHash: approveTx?.hash,
    burnTxHash: burnTx.hash,
    mintTxHash: mintTx.hash,
    messageBytes,
    messageHash,
    attestationSignature,
  };
}

function generateActions(configsSet: CCTPSdkConfigsSet) {
  return {
    configs: configsSet,
    transferUSDC: (options: Omit<TransferUSDCOptions, "configsSet">) => {
      return transferUSDC({ configsSet, ...options });
    },
    approveUSDC: (options: Omit<ApproveUSDCOptions, "configsSet">) => {
      return approveUSDC({ configsSet, ...options });
    },
    burnUSDC: (options: Omit<BurnUSDCOptions, "configsSet">) => {
      return burnUSDC({ configsSet, ...options });
    },
    mintUSDC: (options: Omit<MintUSDCOptions, "configsSet">) => {
      return mintUSDC({ configsSet, ...options });
    },
    approveUSDCOnSolana: (
      options: Omit<ApproveUSDCOnSolanaOptions, "configsSet">
    ) => {
      return approveUSDCOnSolana({ configsSet, ...options });
    },
    burnUSDCOnSolana: (
      options: Omit<BurnUSDCOnSolanaOptions, "configsSet">
    ) => {
      return burnUSDCOnSolana({ configsSet, ...options });
    },
    mintUSDCOnSolana: (
      options: Omit<MintUSDCOnSolanaOptions, "configsSet">
    ) => {
      return mintUSDCOnSolana({ configsSet, ...options });
    },
    isTransferCompleted: (
      options: Omit<IsTransferCompletedOptions, "configsSet">
    ) => {
      const { sourceDomain, ...restOptions } = options;
      const func =
        sourceDomain === CCTPDomain.Solana
          ? isTransferCompletedOnSolana
          : isTransferCompleted;

      return func({ configsSet, sourceDomain, ...restOptions });
    },
    getMessageBytes: (options: Omit<GetMessageBytesOptions, "configsSet">) => {
      return getMessageBytes({ configsSet, ...options });
    },
    fetchAttestation: (
      options: Omit<FetchAttestationOptions, "configsSet">
    ) => {
      return fetchAttestation({ configsSet, ...options });
    },
    waitForAttestation: (
      options: Omit<FetchAttestationOptions, "configsSet">
    ) => {
      return waitForAttestation({ configsSet, ...options });
    },
  };
}
